import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { SubscriptionTier } from '../../api/platform.js';
import { PlatformModal } from './PlatformModal.js';

/**
 * Change-tier operator modal (Phase C-3). Tier select + reason. The
 * "tier must differ from current" precondition is enforced both here (Zod
 * refinement against currentTier) and server-side (C-2 returns 409
 * TIER_UNCHANGED, surfaced via errorMessage).
 */
const TIER_OPTIONS: { value: SubscriptionTier; label: string }[] = [
  { value: 'starter', label: 'Starter' },
  { value: 'growth', label: 'Growth' },
  { value: 'enterprise', label: 'Enterprise' },
];

export function ChangeTierModal({
  open,
  onClose,
  currentTier,
  isSubmitting = false,
  errorMessage,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  currentTier: SubscriptionTier;
  isSubmitting?: boolean;
  errorMessage?: string;
  onConfirm: (body: { toTier: SubscriptionTier; reason: string }) => Promise<void> | void;
}): JSX.Element | null {
  const schema = z.object({
    toTier: z.enum(['starter', 'growth', 'enterprise']),
    reason: z.string().trim().min(1, 'Reason is required').max(500, 'Reason must be 500 characters or fewer'),
  }).refine((v) => v.toTier !== currentTier, {
    path: ['toTier'],
    message: 'Choose a tier different from the current one.',
  });
  type Values = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { toTier: currentTier } });

  if (!open) return null;

  const handleClose = (): void => {
    reset();
    onClose();
  };

  return (
    <PlatformModal
      open={open}
      onClose={handleClose}
      title="Change subscription tier"
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="change-tier-form"
            disabled={isSubmitting}
            className="px-4 py-2 rounded-md font-medium bg-indigo-primary text-white hover:opacity-90 disabled:opacity-50"
          >
            {isSubmitting ? 'Submitting…' : 'Change tier'}
          </button>
        </>
      }
    >
      <form id="change-tier-form" onSubmit={handleSubmit((v) => onConfirm({ toTier: v.toTier, reason: v.reason }))}>
        <p className="text-sm text-gray-700 mb-3">
          Current tier: <span className="font-medium">{currentTier}</span>
        </p>
        <label htmlFor="change-tier-select" className="block text-sm font-medium text-gray-700 mb-1">
          New tier
        </label>
        <select
          id="change-tier-select"
          {...register('toTier')}
          disabled={isSubmitting}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
        >
          {TIER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {errors.toTier ? <p className="mt-1 text-sm text-red-600">{errors.toTier.message}</p> : null}

        <label htmlFor="change-tier-reason" className="block text-sm font-medium text-gray-700 mb-1 mt-3">
          Reason
        </label>
        <textarea
          id="change-tier-reason"
          {...register('reason')}
          disabled={isSubmitting}
          rows={3}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
          placeholder="Written record for the audit trail…"
        />
        {errors.reason ? <p className="mt-1 text-sm text-red-600">{errors.reason.message}</p> : null}
        {errorMessage ? (
          <p className="mt-2 text-sm text-red-600" role="alert">
            {errorMessage}
          </p>
        ) : null}
      </form>
    </PlatformModal>
  );
}
